import { MdKeyboardArrowDown, MdMenu } from 'react-icons/md';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Avatar } from './avatar';
import { DropdownMenu, type DropdownItem } from './dropdown-menu';

export type TopbarProps = {
  /** Zone libre à gauche (fil d'Ariane, recherche…). */
  start?: ReactNode;
  /** Zone libre à droite, avant le bloc utilisateur (badges, notifications…). */
  end?: ReactNode;
  user?: {
    name: string;
    email: string;
    /** Rendu juste après le nom, sur la même ligne (ex. la formule du cabinet). */
    badge?: ReactNode;
  };
  /** Entrées du menu utilisateur. Sans elles, le bloc n'est pas cliquable. */
  userMenuItems?: readonly DropdownItem[];
  /** Ouvre la navigation en drawer sous `lg`. */
  onOpenNav?: () => void;
  className?: string;
};

/** Barre supérieure, partagée par l'app utilisateurs et l'app admin. */
export function Topbar({ start, end, user, userMenuItems, onOpenNav, className }: TopbarProps) {
  const identity = user ? (
    <>
      <Avatar name={user.name} />
      <div className="hidden flex-col gap-0.5 text-left sm:flex">
        <p className="flex items-center gap-1.5 text-xs font-bold text-brand-800">
          {user.name}
          {user.badge ? <span className="font-medium text-brand-800/60">{user.badge}</span> : null}
        </p>
        <p className="text-xs font-medium text-brand-800/70">{user.email}</p>
      </div>
    </>
  ) : null;

  return (
    <header
      className={cn(
        'flex h-16 shrink-0 items-center gap-4 border-b border-border-subtle bg-surface px-4 sm:px-6',
        className,
      )}
    >
      {onOpenNav ? (
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Ouvrir la navigation"
          className="-ml-1 rounded-lg p-2 text-content-muted transition-colors hover:bg-surface-muted lg:hidden"
        >
          <MdMenu className="size-6" aria-hidden="true" />
        </button>
      ) : null}

      <div className="min-w-0 flex-1">{start}</div>

      <div className="flex items-center gap-6">
        {end}

        {user && userMenuItems?.length ? (
          <DropdownMenu
            aria-label="Menu du compte"
            items={userMenuItems}
            header={
              <div className="flex flex-col gap-0.5">
                <p className="text-sm font-semibold text-content">{user.name}</p>
                <p className="text-xs text-content-muted">{user.email}</p>
              </div>
            }
            trigger={
              <>
                {identity}
                <MdKeyboardArrowDown aria-hidden="true" className="size-5 text-content-subtle" />
              </>
            }
          />
        ) : (
          <div className="flex items-center gap-2.5">{identity}</div>
        )}
      </div>
    </header>
  );
}
